import React, {Fragment, useRef, useState} from "react";
import {ArrowDropDown, WorkOutline} from "@mui/icons-material";
import {DialogActions, DialogContent, IconButton, Menu, MenuItem, Tooltip} from "@mui/material";
import Button from "@mui/material/Button";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import FullscreenDialog from "../fullscreen-dialog";
import ModuleMenuItemPlaceholder from "../module-menu-item-placeholder";
import ModulesPlaceholder from "../modules-placeholder";
import {usePackageManager} from "../package-manager";
import {PACKAGE_MANAGER_SOURCE_USER_ITEMS} from "../package-manager/constants";
import UserItemContainersSettingsForm from "../settings-select/user-itemContainers-settings-form";
import ItemContainerSettingsFormProvider from "../settings-select/user-itemContainers-settings-form/ItemContainerSettingsFormProvider";
import UserViewsSettingsForm from "./user-views-settings-form";
import {moduleConfigsSelector} from "../../state/module-configs/moduleConfigsSelectors";
import {nodeSelector} from "../../state/node/nodeSelectors";
import {HEADER_BUTTON_VIEW_MODE_ICON, HEADER_BUTTON_VIEW_MODE_ICONTEXT} from "../header/utils";

const UserItemsSelect = ({
  nodes,
  viewMode,
  onNavigate,
  isViewsVisible,
  isDashboardsVisible,
  isPackageManagerVisible
}) => {
  const {t} = useTranslation();

  const {openPackageManager} = usePackageManager();

  const node = useSelector(nodeSelector);
  const moduleConfigs = useSelector(moduleConfigsSelector);

  const viewsSettingsRef = useRef();
  const dashboardsSettingsRef = useRef();

  const [anchorEl, setanchorEl] = useState(null);

  const [isViewsOpen, setIsViewsOpen] = useState(false);
  const [isDashboardsOpen, setIsDashboardsOpen] = useState(false);

  const handleOpen = event => {
    setanchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setanchorEl(null);
  };

  const onViewsOpen = () => {
    setIsViewsOpen(true);
  };
  const onViewsClose = () => {
    setIsViewsOpen(false);
  };

  const onDashboardsOpen = () => {
    setIsDashboardsOpen(true);
  };
  const onDashboardsClose = () => {
    setIsDashboardsOpen(false);
  };

  return (
    <Fragment>
      <Tooltip title={t("components.userItemsSelect.title")}>
        {viewMode === HEADER_BUTTON_VIEW_MODE_ICON ? (
          <IconButton onClick={handleOpen} aria-label={t("components.userItemsSelect.ariaLabel")} color="inherit">
            <WorkOutline />
          </IconButton>
        ) : (
          <Button
            onClick={handleOpen}
            startIcon={viewMode === HEADER_BUTTON_VIEW_MODE_ICONTEXT ? <WorkOutline /> : null}
            endIcon={<ArrowDropDown />}
            aria-label={t("components.userItemsSelect.ariaLabel")}
            classes={{
              endIcon: "button-select__menu-button__end-icon"
            }}
            sx={{
              background: Boolean(anchorEl) ? "#f5f5f5" : undefined
            }}
          >
            {t("components.userItemsSelect.label")}
          </Button>
        )}
      </Tooltip>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        onClose={handleClose}
        keepMounted
      >
        {isViewsVisible ? (
          <MenuItem
            key="views"
            id="user-items-select__views-btn"
            onClick={() => {
              onViewsOpen();
              handleClose();
            }}
          >
            {t("components.userItemsSelect.menuItems.views")}
          </MenuItem>
        ) : null}
        {isDashboardsVisible ? (
          <MenuItem
            key="dashboards"
            id="user-items-select__dashboards-btn"
            onClick={() => {
              onDashboardsOpen();
              handleClose();
            }}
          >
            {t("components.userItemsSelect.menuItems.dashboards")}
          </MenuItem>
        ) : null}
        {isPackageManagerVisible ? (
          <MenuItem
            key="packages"
            id="user-items-select__packages-btn"
            onClick={() => {
              openPackageManager({
                nodeId: node.nodeId,
                nodeCode: node.code,
                readOnly: false,
                onNavigate: onNavigate,
                source: PACKAGE_MANAGER_SOURCE_USER_ITEMS
              });
              handleClose();
            }}
          >
            {t("components.userItemsSelect.menuItems.packages")}
          </MenuItem>
        ) : null}
        {...(moduleConfigs.placeholders?.["user-items-select-menu"] || []).map(({id, component, fallback}, idx) => (
          <ModuleMenuItemPlaceholder
            key={`module-${idx}`}
            id={id}
            component={component}
            fallback={fallback}
            closeMenu={handleClose}
          />
        ))}
      </Menu>

      <FullscreenDialog
        title={t("components.userItemsSelect.dialog.views.title")}
        open={isViewsOpen}
        onClose={() => {
          if (viewsSettingsRef.current) {
            viewsSettingsRef.current.cancel(() => {
              onViewsClose();
            });
          } else {
            onViewsClose();
          }
        }}
      >
        <DialogContent>
          <UserViewsSettingsForm
            ref={viewsSettingsRef}
            nodes={nodes}
            onNavigate={() => {
              if (viewsSettingsRef.current) {
                viewsSettingsRef.current.cancel(() => {
                  onViewsClose();
                  if (onNavigate) onNavigate();
                });
              } else {
                onViewsClose();
                if (onNavigate) onNavigate();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            className="components__dialog__btn components__dialog__btn--close"
            onClick={() => {
              if (viewsSettingsRef.current) {
                viewsSettingsRef.current.cancel(() => {
                  onViewsClose();
                });
              } else {
                onViewsClose();
              }
            }}
          >
            {t("commons.confirm.close")}
          </Button>
        </DialogActions>
      </FullscreenDialog>

      <FullscreenDialog
        title={t("components.userItemsSelect.dialog.dashboards.title")}
        open={isDashboardsVisible && isDashboardsOpen}
        onClose={() => {
          if (dashboardsSettingsRef.current) {
            dashboardsSettingsRef.current.cancel(() => {
              onDashboardsClose();
            });
          } else {
            onDashboardsClose();
          }
        }}
      >
        <DialogContent>
          <ItemContainerSettingsFormProvider>
            <UserItemContainersSettingsForm
              asDashboard={true}
              ref={dashboardsSettingsRef}
              onNavigate={() => {
                if (dashboardsSettingsRef.current) {
                  dashboardsSettingsRef.current.cancel(() => {
                    onDashboardsClose();
                    if (onNavigate) onNavigate();
                  });
                } else {
                  onDashboardsClose();
                  if (onNavigate) onNavigate();
                }
              }}
            />
          </ItemContainerSettingsFormProvider>
        </DialogContent>
        <DialogActions>
          <Button
            className="components__dialog__btn components__dialog__btn--close"
            onClick={() => {
              if (dashboardsSettingsRef.current) {
                dashboardsSettingsRef.current.cancel(() => {
                  onDashboardsClose();
                });
              } else {
                onDashboardsClose();
              }
            }}
          >
            {t("commons.confirm.close")}
          </Button>
        </DialogActions>
      </FullscreenDialog>

      <ModulesPlaceholder id="user-items-select-dialog" />
    </Fragment>
  );
};

export default UserItemsSelect;
